import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../../contexts/AuthContext';
import Modal, { ConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { categorySchema, type CategoryFormData } from '../../schemas/category';

type GroupType = 'salary' | 'taxes' | 'operational' | 'capital' | 'other';

interface Category {
  id: string;
  name: string;
  nameKey: string | null;
  parentId: string | null;
  groupType: GroupType;
  isActive: boolean;
  createdAt: string;
}

const GROUP_TYPES: GroupType[] = ['salary', 'taxes', 'operational', 'capital', 'other'];

export default function CategoriesPage() {
  const { t } = useTranslation('categories');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [parentFilter, setParentFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');

  const canEdit = user?.role === 'admin' || user?.role === 'accountant';

  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await fetch('/api/admin/categories', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load categories');
      return res.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; parentId?: string | null; groupType?: string; isActive?: boolean }) => {
      const res = await fetch(`/api/admin/categories/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to update category');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/categories/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to delete category');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Category deleted');
      setDeletingCategory(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleToggleActive = (category: Category) => {
    updateMutation.mutate({
      id: category.id,
      isActive: !category.isActive,
    });
  };

  const getCategoryDisplayName = (category: Category): string => {
    if (category.nameKey) {
      const translated = t(`items.${category.nameKey}`, { defaultValue: '' });
      if (translated) return translated;
    }
    return category.name;
  };

  const categories: Category[] = data?.data || [];
  const parentCategories = categories.filter(c => !c.parentId);
  const getChildren = (parentId: string) => categories.filter(c => c.parentId === parentId);

  // Filter categories
  const filteredParentCategories = useMemo(() => {
    let filtered = [...parentCategories];

    // Parent filter - show only specific parent
    if (parentFilter !== 'all') {
      filtered = filtered.filter(c => c.id === parentFilter);
    }

    // Group type filter
    if (groupFilter !== 'all') {
      // Include parents that have the group type OR have children with that group type
      const parentsWithMatchingChildren = new Set<string>();
      categories.forEach(c => {
        if (c.groupType === groupFilter) {
          if (c.parentId) {
            parentsWithMatchingChildren.add(c.parentId);
          } else {
            parentsWithMatchingChildren.add(c.id);
          }
        }
      });
      filtered = filtered.filter(c => parentsWithMatchingChildren.has(c.id));
    }

    // Search filter
    if (search) {
      const s = search.toLowerCase();
      const matchingParentIds = new Set<string>();

      categories.forEach(c => {
        const displayName = getCategoryDisplayName(c).toLowerCase();
        if (displayName.includes(s)) {
          if (c.parentId) {
            matchingParentIds.add(c.parentId);
          } else {
            matchingParentIds.add(c.id);
          }
        }
      });

      filtered = filtered.filter(c => matchingParentIds.has(c.id));
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(c => c.isActive === isActive);
    }

    return filtered;
  }, [parentCategories, categories, search, statusFilter, parentFilter, groupFilter]);

  const getFilteredChildren = (parentId: string) => {
    let children = getChildren(parentId);

    // Group type filter
    if (groupFilter !== 'all') {
      children = children.filter(c => c.groupType === groupFilter);
    }

    if (search) {
      const s = search.toLowerCase();
      children = children.filter(c => getCategoryDisplayName(c).toLowerCase().includes(s));
    }

    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      children = children.filter(c => c.isActive === isActive);
    }

    return children;
  };

  const totalFilteredCount = useMemo(() => {
    let count = filteredParentCategories.length;
    filteredParentCategories.forEach(p => {
      count += getFilteredChildren(p.id).length;
    });
    return count;
  }, [filteredParentCategories, search, statusFilter, groupFilter]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('title')}</h1>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            {t('addCategory')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          className="form-input !w-auto min-w-[220px]"
          placeholder="Search categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-input !w-auto min-w-[150px]"
          value={parentFilter}
          onChange={(e) => setParentFilter(e.target.value)}
        >
          <option value="all">{tc('all')} {t('parent')}</option>
          {parentCategories.map((c) => (
            <option key={c.id} value={c.id}>{getCategoryDisplayName(c)}</option>
          ))}
        </select>
        <select
          className="form-input !w-auto min-w-[150px]"
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
        >
          <option value="all">{tc('all')} {t('groupType')}</option>
          {GROUP_TYPES.map((gt) => (
            <option key={gt} value={gt}>{t(`groups.${gt}`)}</option>
          ))}
        </select>
        <select
          className="form-input !w-auto min-w-[150px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{tc('all')} Status</option>
          <option value="active">{tc('status.active')}</option>
          <option value="inactive">{tc('status.inactive')}</option>
        </select>
        <span className="ml-auto text-sm text-foreground-muted">{totalFilteredCount} categories</span>
      </div>

      {isLoading ? (
        <p className="text-muted">{tc('actions.loading')}</p>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('groupType')}</th>
                <th>{t('parent')}</th>
                <th>{t('status')}</th>
                {canEdit && <th>{tc('actions.label')}</th>}
              </tr>
            </thead>
            <tbody>
              {filteredParentCategories.map((category) => (
                <>
                  <tr key={category.id}>
                    <td><strong>{getCategoryDisplayName(category)}</strong></td>
                    <td><span className={`badge badge-group-${category.groupType}`}>{t(`groups.${category.groupType}`)}</span></td>
                    <td className="text-muted">-</td>
                    <td>
                      <span
                        className={`badge ${category.isActive ? 'badge-closed' : 'badge-open'}`}
                        style={{ cursor: canEdit ? 'pointer' : 'default' }}
                        onClick={() => canEdit && handleToggleActive(category)}
                      >
                        {category.isActive ? tc('status.active') : tc('status.inactive')}
                      </span>
                    </td>
                    {canEdit && (
                      <td>
                        <button className="btn btn-secondary px-2 py-1 text-xs mr-2" onClick={() => setEditingCategory(category)}>
                          {tc('actions.edit')}
                        </button>
                        <button className="btn btn-danger px-2 py-1 text-xs" onClick={() => setDeletingCategory(category)}>
                          {tc('actions.delete')}
                        </button>
                      </td>
                    )}
                  </tr>
                  {getFilteredChildren(category.id).map((child) => (
                    <tr key={child.id} className="bg-background">
                      <td className="pl-8">{getCategoryDisplayName(child)}</td>
                      <td><span className={`badge badge-group-${child.groupType}`}>{t(`groups.${child.groupType}`)}</span></td>
                      <td className="text-muted">{getCategoryDisplayName(category)}</td>
                      <td>
                        <span
                          className={`badge ${child.isActive ? 'badge-closed' : 'badge-open'} ${canEdit ? 'cursor-pointer' : ''}`}
                          onClick={() => canEdit && handleToggleActive(child)}
                        >
                          {child.isActive ? tc('status.active') : tc('status.inactive')}
                        </span>
                      </td>
                      {canEdit && (
                        <td>
                          <button className="btn btn-secondary px-2 py-1 text-xs mr-2" onClick={() => setEditingCategory(child)}>
                            {tc('actions.edit')}
                          </button>
                          <button className="btn btn-danger px-2 py-1 text-xs" onClick={() => setDeletingCategory(child)}>
                            {tc('actions.delete')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              ))}
              {filteredParentCategories.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="text-center text-muted">
                    {categories.length === 0 ? t('noCategories') : 'No matching categories'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <CategoryFormModal
          categories={parentCategories}
          getCategoryDisplayName={getCategoryDisplayName}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            setShowForm(false);
            toast.success('Category created');
          }}
        />
      )}

      {editingCategory && (
        <CategoryFormModal
          category={editingCategory}
          categories={parentCategories.filter(c => c.id !== editingCategory.id)}
          getCategoryDisplayName={getCategoryDisplayName}
          onClose={() => setEditingCategory(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            setEditingCategory(null);
            toast.success('Category updated');
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        onConfirm={() => deletingCategory && deleteMutation.mutate(deletingCategory.id)}
        title="Delete Category"
        message={`Are you sure you want to delete "${deletingCategory ? getCategoryDisplayName(deletingCategory) : ''}"? This cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

    </div>
  );
}

function CategoryFormModal({
  category,
  categories,
  getCategoryDisplayName,
  onClose,
  onSuccess,
}: {
  category?: Category;
  categories: Category[];
  getCategoryDisplayName: (c: Category) => string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('categories');
  const { t: tc } = useTranslation('common');
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: category?.name || '',
      parentId: category?.parentId || '',
      groupType: category?.groupType || 'other',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const res = await fetch('/api/admin/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name,
          parentId: data.parentId || undefined,
          groupType: data.groupType,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to create category');
      }
      return res.json();
    },
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const res = await fetch(`/api/admin/categories/${category!.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name,
          parentId: data.parentId || null,
          groupType: data.groupType,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to update category');
      }
      return res.json();
    },
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const onSubmit = (data: CategoryFormData) => {
    if (category) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={category ? t('editCategory') : t('newCategory')}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">{t('name')}</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            {...register('name')}
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('parent')}</label>
          <select className="form-input" {...register('parentId')}>
            <option value="">{t('parentNone')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{getCategoryDisplayName(c)}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">{t('groupType')}</label>
          <select className="form-input" {...register('groupType')}>
            {GROUP_TYPES.map((gt) => (
              <option key={gt} value={gt}>{t(`groups.${gt}`)}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isPending}>
            {isPending ? tc('actions.loading') : (category ? tc('actions.save') : tc('actions.create'))}
          </button>
        </div>
      </form>
    </Modal>
  );
}
